<script setup lang="ts">
import { ButtonComponent, Card, FlexComponent, SwitchComponent, TypographyComponent } from '@metapic-apps/shared'
import { ElNotification } from 'element-plus'
import { computed, ref, watch } from 'vue'

import { useUpdateAdvertiser } from '@/api/advertiser.api'
import { CATEGORIES } from '@/lib/constants/categories'
import type { AdvertiserType } from '@/lib/types/advertiser.types'

const props = defineProps<{
  advertiserId: string
  storeCategories: AdvertiserType['storeCategories']
}>()

const selectedCategoryIds = ref<Set<number>>(new Set())

watch(
  () => props.storeCategories,
  (categories) => {
    selectedCategoryIds.value = new Set(categories.map((c) => c.id))
  },
  { immediate: true, deep: true },
)

const { loading, error, updateAdvertiser } = useUpdateAdvertiser(computed(() => props.advertiserId))

const hasSelection = computed(() => selectedCategoryIds.value.size > 0)

const isSelected = (id: number) => selectedCategoryIds.value.has(id)

const toggleCategory = (id: number, value: boolean) => {
  const newSet = new Set(selectedCategoryIds.value)
  if (value) {
    newSet.add(id)
  } else {
    newSet.delete(id)
  }
  selectedCategoryIds.value = newSet
}

const save = async () => {
  await updateAdvertiser({ categories: Array.from(selectedCategoryIds.value) })
  if (!error.value) {
    ElNotification.success('Categories saved successfully')
  }
}
</script>

<template>
  <Card title="Categories" title-size="small" class="categories-settings">
    <div class="categories-settings__list">
      <FlexComponent
        v-for="category in CATEGORIES"
        :key="category.id"
        direction="row"
        align="center"
        :gap="12"
        class="categories-settings__item"
      >
        <component :is="category.icon" class="categories-settings__icon" aria-hidden="true" />
        <span class="categories-settings__name">{{ category.name }}</span>
        <SwitchComponent :model-value="isSelected(category.id)" @update:model-value="(val) => toggleCategory(category.id, val)" />
      </FlexComponent>
    </div>

    <TypographyComponent v-if="!hasSelection" variant="body-small" class="categories-settings__error">
      At least one category must be selected.
    </TypographyComponent>
    <template #footer>
      <ButtonComponent
        variant="accent"
        :is-loading="loading"
        :disabled="!hasSelection"
        class="categories-settings__save-button"
        @click="save"
      >
        Save
      </ButtonComponent>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.categories-settings {
  &__list {
    display: flex;
    flex-direction: column;
  }

  &__item {
    position: relative;
    padding: 8px 12px;
    border-radius: 16px;

    &:hover {
      cursor: pointer;

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: var(--primary);
        opacity: 0.02;
        pointer-events: none;
      }
    }
  }

  &__icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &__name {
    flex: 1;
    font-family: Urbanist, sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }

  &__error {
    color: var(--danger);
  }

  &__save-button {
    align-self: flex-start;
  }
}
</style>
